import { useQuery } from "@tanstack/react-query";
import { tenantDocumentTypesApi } from "../api/tenantDocumentTypesApi.js";
import { getDocTypeLabel } from "../api.js";

/**
 * tenant_document_types 一覧を1回フェッチする共通クエリ。
 * 設計書: docs/design/document-type-master-phase2.md §5.2
 *
 * これまで `apps/web/src/lib/issuerMaster.ts` の `PROFESSION_BY_DOC_TYPE` /
 * `PROFESSION_FULL_LABEL`（組み込み4書類種別に固定した静的マップ）が担っていた
 * 「書類種別 → 発行者職能」の対応を、DBから取得したクエリ結果ベースに置き換える。
 */
export function useTenantDocumentTypes() {
  return useQuery({
    queryKey: ["tenant-document-types"],
    queryFn: () => tenantDocumentTypesApi.list(),
    staleTime: 5 * 60_000,
  });
}

/** 書類種別 → 発行者職能キー（tenant_document_types.profession）。未登録/発行者なしなら null */
export function useDocTypeProfession(docType: string | null | undefined): {
  profession: string | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useTenantDocumentTypes();
  if (!docType) return { profession: null, isLoading };
  const row = data?.data.find((d) => d.doc_type === docType) ?? null;
  return { profession: row?.profession ?? null, isLoading };
}

/**
 * 書類種別の表示ラベル。`tenant_document_types.label` を優先し、まだクエリ結果が
 * 無い場合（読み込み中等）のみ `getDocTypeLabel()` の静的フォールバック
 * （組み込み7種の固定ラベル、カスタム種別は `custom_` を除去した16進数）を使う。
 * 設計書: docs/design/document-type-master-phase2.md §8（reviewer 指摘対応）。
 */
export function useDocTypeLabel(docType: string | null | undefined): string {
  const { data } = useTenantDocumentTypes();
  if (!docType) return "";
  const row = data?.data.find((d) => d.doc_type === docType);
  return row?.label ?? getDocTypeLabel(docType);
}
